import React, { useState } from 'react';
import { Send, Mail, MessageCircle, CheckCircle } from 'lucide-react';

const SendInvitation = ({ onClose, onSend }) => {
  const [invitationType, setInvitationType] = useState('email');
  const [contactValue, setContactValue] = useState('');
  const [error, setError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleInvitationTypeChange = (type) => {
    setInvitationType(type);
    setContactValue('');
    setError('');
    setSuccessMessage('');
  };

  const validateInput = () => {
    console.log('Validating input for', invitationType, ':', contactValue);
    if (!contactValue.trim()) {
      setError(invitationType === 'email' ? 'Email is required' : 'Phone number is required');
      return false;
    }

    if (invitationType === 'email') {
      const emailRegex = /\S+@\S+\.\S+/;
      if (!emailRegex.test(contactValue)) {
        setError('Please enter a valid email address');
        return false;
      }
    } else {
      const phoneRegex = /^[\d\s+\-()]+$/;
      if (!phoneRegex.test(contactValue)) {
        setError('Please enter a valid phone number');
        return false;
      }
    }
    return true;
  };

  const handleSend = async () => {
    if (!validateInput()) return;

    setIsSending(true);
    setError('');
    setSuccessMessage('');
    console.log('Sending invitation via', invitationType, 'to', contactValue);
    
    try {
      await onSend({
        type: invitationType,
        contact: contactValue,
        timestamp: new Date().toISOString()
      });
      
      // Show success message inside modal
      setSuccessMessage(`Invitation sent successfully to ${contactValue}`);
      setIsSending(false);
      
      // Close modal after 3 seconds (giving user time to see the success message)
      setTimeout(() => {
        onClose();
      }, 3000);
      
    } catch (err) {
      setIsSending(false);
      console.error('SendInvitation error:', err);
      
      // Extract the actual error message
      const errorMessage = err.message || err.error || 'Unknown error occurred';
      
      // Only show specific backend errors inside the modal
      if (errorMessage.toLowerCase().includes('already been sent') ||
          errorMessage.toLowerCase().includes('already exists') ||
          errorMessage.toLowerCase().includes('invalid') ||
          errorMessage.toLowerCase().includes('not found') ||
          errorMessage.toLowerCase().includes('required')) {
        setError(errorMessage);
      }
    }
  };

  return (
    <div className="template-modal-overlay">
      <div className="template-modal">
        <div className="modal-header">
          <h3>Send Invitation</h3>
          <button className="modal-close" onClick={onClose} disabled={isSending || successMessage}>
            ×
          </button>
        </div>

        <div className="template-list">
          {/* Success Message (inside modal) */}
          {successMessage && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#d1fae5',
              border: '1px solid #10b981',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <CheckCircle size={24} color="#10b981" />
              <div style={{ flex: 1 }}>
                <p style={{ 
                  margin: 0, 
                  color: '#065f46', 
                  fontWeight: '500',
                  fontSize: '0.9375rem'
                }}>
                  {successMessage}
                </p>
                <p style={{ 
                  margin: '0.25rem 0 0 0', 
                  color: '#059669', 
                  fontSize: '0.8125rem'
                }}>
                  This window will close automatically...
                </p>
              </div>
            </div>
          )}

          {/* Form (hidden when success message is shown) */}
          {!successMessage && (
            <>
              {/* Invitation Type Selection */}
              <div className="form-group">
                <label className="form-label">Select Invitation Method</label>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.875rem 1rem',
                    border: '2px solid',
                    borderColor: invitationType === 'email' ? '#2563eb' : '#e5e7eb',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    background: invitationType === 'email' ? '#eff6ff' : 'white',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="invitationType"
                      value="email"
                      checked={invitationType === 'email'}
                      onChange={(e) => handleInvitationTypeChange(e.target.value)}
                      style={{ width: '1.125rem', height: '1.125rem', cursor: 'pointer', accentColor: '#2563eb' }}
                    />
                    <Mail size={20} color="#2563eb" />
                    <span style={{ fontSize: '0.9375rem', fontWeight: '500', color: '#111827' }}>
                      Email
                    </span>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.875rem 1rem',
                    border: '2px solid',
                    borderColor: invitationType === 'whatsapp' ? '#16a34a' : '#e5e7eb',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    background: invitationType === 'whatsapp' ? '#f0fdf4' : 'white',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="invitationType"
                      value="whatsapp"
                      checked={invitationType === 'whatsapp'}
                      onChange={(e) => handleInvitationTypeChange(e.target.value)}
                      style={{ width: '1.125rem', height: '1.125rem', cursor: 'pointer', accentColor: '#16a34a' }}
                    />
                    <MessageCircle size={20} color="#16a34a" />
                    <span style={{ fontSize: '0.9375rem', fontWeight: '500', color: '#111827' }}>
                      WhatsApp
                    </span>
                  </label>
                </div>
              </div>

              {/* Contact Input */}
              <div className="form-group">
                <label className="form-label">
                  {invitationType === 'email' ? 'Email Address' : 'Phone Number'} *
                </label>
                <input
                  type={invitationType === 'email' ? 'email' : 'tel'}
                  value={contactValue}
                  onChange={(e) => {
                    setContactValue(e.target.value);
                    setError('');
                  }}
                  placeholder={invitationType === 'email' ? 'john@example.com' : '+44 7700 900000'}
                  disabled={isSending}
                  className={`form-input ${error ? 'error' : ''}`}
                />
                {error && (
                  <span className="error-text" style={{
                    display: 'block',
                    marginTop: '0.5rem',
                    fontSize: '0.875rem',
                    color: '#ef4444'
                  }}>
                    {error}
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="form-actions">
                <button 
                  type="button"
                  onClick={onClose}
                  disabled={isSending}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={isSending}
                  className="btn-save"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Send size={18} />
                  {isSending ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendInvitation;